import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';

const ProblemSection = () => {
  const [sectionVisible, setSectionVisible] = useState(false);
  const [cardsVisible, setCardsVisible] = useState([]);
  const [impactVisible, setImpactVisible] = useState(false);
  const [bgParallaxY, setBgParallaxY] = useState(0);
  const sectionRef = useRef(null);

  const patterns = [
    {
      id: 1,
      thought: "I'm not good enough for this.",
      icon: "Frown",
      description: "A thought you didn't choose, but your mind keeps repeating."
    },
    {
      id: 2,
      thought: "Everyone can see I don't belong here.",
      icon: "Users",
      description: "A loop that plays in the background, shaping how you show up."
    },
    {
      id: 3,
      thought: "I always mess things up.",
      icon: "AlertCircle",
      description: "A pattern installed through repetition — not a fact about who you are."
    },
    {
      id: 4,
      thought: "I don't deserve what I want.",
      icon: "Heart",
      description: "A belief that blocks you from even trying."
    }
  ];

  const [currentPattern, setCurrentPattern] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setSectionVisible(true);
          [0, 1, 2].forEach((i) => {
            setTimeout(() => setCardsVisible(prev => [...prev, i]), i * 180);
          });
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );

    const impactObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setImpactVisible(true);
          impactObserver.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
      const impactEl = sectionRef.current.querySelector('#impact-statement');
      if (impactEl) impactObserver.observe(impactEl);
    }

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!prefersReduced) {
      const handleScroll = () => {
        if (sectionRef.current) {
          const rect = sectionRef.current.getBoundingClientRect();
          setBgParallaxY(rect.top * -0.15);
        }
      };
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => { observer?.disconnect(); window.removeEventListener('scroll', handleScroll); };
    }

    return () => observer?.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPattern(prev => (prev + 1) % patterns.length);
    }, 3200);
    return () => clearInterval(interval);
  }, []);

  const pillars = [
    {
      icon: "Brain",
      title: "Mind Reframing",
      description: "Replace negative loops by installing new thoughts that feel believable in your own voice.",
      color: "from-violet-500/20 to-purple-500/20",
      iconColor: "text-violet-400"
    },
    {
      icon: "Mic",
      title: "Your Voice as Guide",
      description: "Hearing yourself say the new thought makes it familiar. The brain learns through repetition in a voice it trusts.",
      color: "from-blue-500/20 to-cyan-500/20",
      iconColor: "text-blue-400"
    },
    {
      icon: "RefreshCw",
      title: "Personalized Daily Rituals",
      description: "Guidance shaped by your real situation, repeated daily until the new thought becomes the default.",
      color: "from-emerald-500/20 to-teal-500/20",
      iconColor: "text-emerald-400"
    }
  ];

  return (
    <section id="problem" ref={sectionRef} className="relative py-20 px-6 overflow-hidden">
      {/* Panoramic Image Band */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ transform: `translateY(${bgParallaxY}px)`, willChange: 'transform' }}
      >
        <img
          src="/image1.jpg"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover object-center opacity-[0.15] scale-110"
        />
      </div>
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-background/50 via-transparent to-background/50" />
      <div className="relative z-10 max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
            Your mind is not broken.{' '}
            <span className="text-gradient">It's programmed.</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            The negative loops you experience aren't character flaws — they're patterns your mind learned.
            And what was learned can be replaced.
          </p>
        </div>

        {/* Thought Pattern Carousel */}
        <div className="relative mb-16">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-semibold text-foreground mb-2">
              Sound familiar?
            </h3>
            <p className="text-muted-foreground text-sm">
              These aren't personality traits. They're installed programs.
            </p>
          </div>

          <div className="relative min-h-[280px] flex items-center justify-center">
            {patterns.map((item, index) => (
              <div
                key={item.id}
                className={`absolute transition-all duration-500 ${
                  index === currentPattern
                    ? 'opacity-100 scale-100 z-10'
                    : 'opacity-20 scale-95 z-0'
                }`}
                style={{
                  transform: `translateX(${(index - currentPattern) * 20}px) translateY(${Math.sin((index - currentPattern) * 0.5) * 10}px)`
                }}
              >
                <div className="glassmorphism p-8 rounded-3xl max-w-md mx-auto text-center">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary/20 flex items-center justify-center">
                    <Icon name={item.icon} size={24} className="text-primary" />
                  </div>
                  <blockquote className="text-lg font-medium text-foreground mb-4 italic">
                    "{item.thought}"
                  </blockquote>
                  <p className="text-sm text-muted-foreground">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-center space-x-2 mt-8">
            {patterns.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentPattern(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentPattern
                    ? 'bg-primary scale-125'
                    : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                }`}
              />
            ))}
          </div>
        </div>

        {/* How DepthMe Works: 3 Pillars */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {pillars.map((pillar, index) => (
            <div
              key={index}
              className={`glassmorphism p-6 rounded-2xl transition-all duration-700 ${
                cardsVisible.includes(index) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: `${index * 120}ms` }}
            >
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${pillar.color} flex items-center justify-center mb-4`}>
                <Icon name={pillar.icon} size={22} className={pillar.iconColor} />
              </div>
              <h4 className="text-lg font-semibold text-foreground mb-2">{pillar.title}</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">{pillar.description}</p>
            </div>
          ))}
        </div>

        {/* Impact Statement */}
        <div id="impact-statement" className={`text-center transition-all duration-1000 ${impactVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="glassmorphism p-8 rounded-3xl max-w-4xl mx-auto">
            <Icon name="Repeat" size={40} className="text-primary mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-foreground mb-4">
              The more you repeat it, the more it becomes you.
            </h3>
            <p className="text-lg text-muted-foreground leading-relaxed">
              DepthMe combines mind reframing, your own voice, and personalized daily rituals to replace old patterns —
              not by fighting them, but by building new ones that stick.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
